import { useMemo, useState } from 'react';
import axios from 'axios';
import {
  RefreshCw, Folder, File as FileIcon, Link2,
  ChevronDown, ChevronRight, Trash2, Download
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────
// Formatting helpers
// ─────────────────────────────────────────────────────────────────────

function formatSize(bytes, type) {
  if (type === 'folder') return '';
  if (bytes == null) return '';
  if (bytes < 1024)                 return `${bytes} B`;
  if (bytes < 1024 * 1024)          return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)   return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatModifyTime(ms) {
  if (!ms) return '';
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function iconFor(type) {
  if (type === 'folder') return <Folder size={14} strokeWidth={1.75} color="#0e7490" />;
  if (type === 'link')   return <Link2  size={14} strokeWidth={1.75} color="#6b7280" />;
  return <FileIcon size={14} strokeWidth={1.75} color="#6b7280" />;
}

// Extract the uppercase file extension (everything after the LAST dot).
// "report.20260518.trx" → "TRX". Dotless names → "" (no extension).
function getExt(name) {
  if (!name) return '';
  const i = name.lastIndexOf('.');
  if (i < 0 || i === name.length - 1) return '';
  return name.slice(i + 1).toUpperCase();
}

// CSV builder — quote any field that contains a comma, quote, or
// newline; escape internal quotes by doubling them. Returns a string
// ready to feed into a Blob.
function toCsv(rows) {
  const escape = (v) => {
    if (v == null) return '';
    const s = String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return rows.map((r) => r.map(escape).join(',')).join('\r\n') + '\r\n';
}

// Walk the loaded folder tree and yield one row per file, applying the
// active extension filter. Used for both the CSV download and for the
// visible row count next to each folder header.
function collectFiles(folders, extFilter) {
  const rows = [];
  function visit(folderName, folderPath, entries) {
    if (!entries) return;
    for (const e of entries) {
      if (e.type === 'folder') {
        // Recurse into subfolders that the server expanded.
        const childPath = `${folderPath}/${e.name}`;
        if (e.entries) visit(folderName, childPath, e.entries);
        continue;
      }
      const ext = getExt(e.name);
      if (extFilter !== 'ALL' && ext !== extFilter) continue;
      rows.push({
        folder: folderName,
        path:   `${folderPath}/${e.name}`,
        name:   e.name,
        ext,
        size:   e.size,
        modify_time: e.modify_time
      });
    }
  }
  for (const [name, f] of Object.entries(folders || {})) {
    if (f && Array.isArray(f.entries)) visit(name, f.path, f.entries);
  }
  return rows;
}

// All distinct uppercase extensions present across both folders. Used
// to populate the file-type filter dropdown so it shows what's actually
// there (in addition to the always-present TRX / SFT / PRX defaults).
function collectExtensions(folders) {
  const seen = new Set();
  function visit(entries) {
    for (const e of entries || []) {
      if (e.type === 'folder') { if (e.entries) visit(e.entries); continue; }
      const ext = getExt(e.name);
      if (ext) seen.add(ext);
    }
  }
  for (const f of Object.values(folders || {})) {
    if (f && Array.isArray(f.entries)) visit(f.entries);
  }
  return [...seen].sort();
}

// Sort a flat array of entries. Folders always sort first within their
// section so the user can still find them when sorting by size/date.
function sortEntries(entries, sortBy, sortDir) {
  const sign = sortDir === 'desc' ? -1 : 1;
  return [...entries].sort((a, b) => {
    if (a.type === 'folder' && b.type !== 'folder') return -1;
    if (a.type !== 'folder' && b.type === 'folder') return 1;
    if (sortBy === 'size') return sign * ((a.size ?? 0) - (b.size ?? 0));
    if (sortBy === 'date') return sign * ((a.modify_time ?? 0) - (b.modify_time ?? 0));
    return sign * a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true });
  });
}

// ─────────────────────────────────────────────────────────────────────
// Row components
// ─────────────────────────────────────────────────────────────────────

// One file/folder row inside a folder's listing table. Files get a
// checkbox; folders do not (delete is files-only).
function EntryRow({ entry, fullPath, isSelected, onToggleSelect }) {
  const isFile = entry.type !== 'folder';
  return (
    <tr>
      <td className="sftp-row-check">
        {isFile && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(fullPath)}
            aria-label={`Select ${entry.name}`}
          />
        )}
      </td>
      <td>{iconFor(entry.type)}</td>
      <td className="cell-mono">
        {entry.name}{entry.type === 'folder' ? '/' : ''}
      </td>
      <td className="cell-mono">{getExt(entry.name)}</td>
      <td className="cell-mono">{formatSize(entry.size, entry.type)}</td>
      <td className="cell-mono">{formatModifyTime(entry.modify_time)}</td>
    </tr>
  );
}

function EntryTable({ entries, parentPath, sortBy, sortDir, extFilter, selected, onToggleSelect }) {
  // Apply filter (files only — folders always render so users can drill
  // into nested subdirs). Then sort.
  const visible = entries.filter((e) => {
    if (e.type === 'folder') return true;
    if (extFilter === 'ALL') return true;
    return getExt(e.name) === extFilter;
  });
  const sorted = sortEntries(visible, sortBy, sortDir);

  if (sorted.length === 0) {
    return (
      <p className="empty-tab" style={{ margin: 0, padding: 'var(--space-3)' }}>
        {extFilter === 'ALL' ? 'This folder is empty.' : `No ${extFilter} files in this folder.`}
      </p>
    );
  }
  return (
    <table className="data-table sftp-listing-table">
      <thead>
        <tr>
          <th className="sftp-row-check"></th>
          <th style={{ width: '24px' }}></th>
          <th>Name</th>
          <th style={{ width: '60px' }}>Type</th>
          <th style={{ width: '90px' }}>Size</th>
          <th style={{ width: '140px' }}>Last Modified</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((e) => (
          <EntryRow
            key={`${parentPath}/${e.name}`}
            entry={e}
            fullPath={`${parentPath}/${e.name}`}
            isSelected={selected.has(`${parentPath}/${e.name}`)}
            onToggleSelect={onToggleSelect}
          />
        ))}
      </tbody>
    </table>
  );
}

// Nested subfolders inside Send/Receive — one level deep. Each is
// independently collapsible.
function NestedSubfolders({ entries, parentPath, sortBy, sortDir, extFilter, selected, onToggleSelect, collapsedSubs, toggleSub }) {
  const subfolders = (entries || []).filter((e) => e.type === 'folder' && (e.entries || e.entries_error));
  if (subfolders.length === 0) return null;
  return (
    <div className="sftp-subfolders">
      {subfolders.map((sub) => {
        const subPath = `${parentPath}/${sub.name}`;
        const isOpen = !collapsedSubs.has(subPath);
        const Chev = isOpen ? ChevronDown : ChevronRight;
        return (
          <section className="sftp-subfolder" key={sub.name}>
            <header className="sftp-folder-head sftp-folder-head-clickable" onClick={() => toggleSub(subPath)}>
              <Chev size={14} strokeWidth={2} />
              <h5>{sub.name}/</h5>
              <span className="sftp-folder-count">
                <span className="cell-mono">{subPath}</span>
                {!sub.entries_error && (
                  <>{' · '}{sub.entries.length} {sub.entries.length === 1 ? 'item' : 'items'}</>
                )}
              </span>
            </header>
            {isOpen && (sub.entries_error ? (
              <p className="error" style={{ margin: 'var(--space-2) 0 0' }}>
                Couldn't list this folder: {sub.entries_error}
              </p>
            ) : (
              <EntryTable
                entries={sub.entries}
                parentPath={subPath}
                sortBy={sortBy} sortDir={sortDir}
                extFilter={extFilter}
                selected={selected} onToggleSelect={onToggleSelect}
              />
            ))}
          </section>
        );
      })}
    </div>
  );
}

function FolderSection({ title, folder, missingNote, isOpen, onToggle, sortBy, sortDir, extFilter, selected, onToggleSelect, collapsedSubs, toggleSub }) {
  const Chev = isOpen ? ChevronDown : ChevronRight;
  if (!folder) {
    return (
      <section className="sftp-folder">
        <header className="sftp-folder-head sftp-folder-head-clickable" onClick={onToggle}>
          <Chev size={14} strokeWidth={2} />
          <h4>{title}</h4>
          <span className="sftp-folder-count cell-muted">— folder not found</span>
        </header>
        {isOpen && (
          <p className="empty-tab" style={{ margin: 0, padding: 'var(--space-3)' }}>
            {missingNote || `No /${title} folder on this server.`}
          </p>
        )}
      </section>
    );
  }
  if (folder.error) {
    return (
      <section className="sftp-folder">
        <header className="sftp-folder-head sftp-folder-head-clickable" onClick={onToggle}>
          <Chev size={14} strokeWidth={2} />
          <h4>{title}</h4>
          <span className="sftp-folder-count cell-muted">{folder.path}</span>
        </header>
        {isOpen && (
          <p className="error" style={{ margin: 'var(--space-2) 0 0' }}>
            Couldn't list this folder: {folder.error}
          </p>
        )}
      </section>
    );
  }
  const count = folder.entries.length;
  return (
    <section className="sftp-folder">
      <header className="sftp-folder-head sftp-folder-head-clickable" onClick={onToggle}>
        <Chev size={14} strokeWidth={2} />
        <h4>{title}</h4>
        <span className="sftp-folder-count">
          <span className="cell-mono">{folder.path}</span>
          {' · '}
          {count} {count === 1 ? 'item' : 'items'}
        </span>
      </header>
      {isOpen && (
        <>
          <EntryTable
            entries={folder.entries}
            parentPath={folder.path}
            sortBy={sortBy} sortDir={sortDir}
            extFilter={extFilter}
            selected={selected} onToggleSelect={onToggleSelect}
          />
          <NestedSubfolders
            entries={folder.entries}
            parentPath={folder.path}
            sortBy={sortBy} sortDir={sortDir}
            extFilter={extFilter}
            selected={selected} onToggleSelect={onToggleSelect}
            collapsedSubs={collapsedSubs} toggleSub={toggleSub}
          />
        </>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Delete-confirmation dialog
// ─────────────────────────────────────────────────────────────────────

function DeleteConfirmDialog({ open, paths, onCancel, onConfirm, busy, error }) {
  const [typed, setTyped] = useState('');
  if (!open) return null;
  const ready = typed.trim().toUpperCase() === 'DELETE';
  return (
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}>
      <div className="modal-card" role="dialog" aria-modal="true" aria-label="Confirm SFTP delete">
        <header className="modal-head">
          <h3>Delete {paths.length} file{paths.length === 1 ? '' : 's'} from SFTP</h3>
          <button type="button" className="modal-close" onClick={onCancel} disabled={busy} aria-label="Close">×</button>
        </header>
        <div className="modal-body">
          <p className="modal-sub" style={{ marginTop: 0 }}>
            This is permanent — the files will be removed from the SFTP server immediately
            and cannot be recovered. Review the list, then type <code>DELETE</code> to enable
            the button.
          </p>
          <div className="sftp-delete-list">
            {paths.map((p) => <div key={p} className="cell-mono">{p}</div>)}
          </div>
          <label className="form-field" style={{ marginTop: 'var(--space-3)' }}>
            <span>Type DELETE to confirm</span>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoFocus
              className="cell-mono"
            />
          </label>
          {error && <p className="error" style={{ marginTop: 'var(--space-3)' }}>{error}</p>}
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
            <button
              type="button"
              className="btn-primary"
              onClick={onConfirm}
              disabled={!ready || busy}
              style={!ready || busy ? undefined : { background: 'var(--color-bad)', borderColor: 'var(--color-bad)' }}
            >
              {busy ? 'Deleting…' : `Delete ${paths.length} file${paths.length === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Main browser
// ─────────────────────────────────────────────────────────────────────

export default function SftpFolderBrowser({ data, onRefreshed }) {
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Per-folder collapse state. Send / Receive default open; nested
  // subfolders also default open (start expanded so the user sees
  // everything; collapsing is a one-click action).
  const [collapsedTop, setCollapsedTop] = useState(new Set());     // 'Send' | 'Receive'
  const [collapsedSubs, setCollapsedSubs] = useState(new Set());   // full sub paths

  // Multi-select state — full paths of currently-selected FILES.
  const [selected, setSelected] = useState(new Set());

  // Filter + sort UI state.
  const [extFilter, setExtFilter] = useState('ALL');
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  // Delete dialog state.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const folders = data?.folders || {};

  // Discovered extensions across the data — feeds the filter dropdown.
  const knownExts = useMemo(() => collectExtensions(folders), [folders]);
  // Always-present options + anything else the data showed us.
  const filterOptions = useMemo(() => {
    const merged = new Set(['TRX', 'SFT', 'PRX', ...knownExts]);
    return ['ALL', ...[...merged].sort()];
  }, [knownExts]);

  // Selection helpers.
  function toggleSelect(path) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path); else next.add(path);
      return next;
    });
  }
  function clearSelection() { setSelected(new Set()); }

  // Collapse helpers.
  function toggleTop(name) {
    setCollapsedTop((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  }
  function toggleSub(path) {
    setCollapsedSubs((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path); else next.add(path);
      return next;
    });
  }

  async function handleRefresh() {
    setRefreshing(true);
    setError(null);
    try {
      const res = await axios.post('/api/bulloch/sftp-list', {
        configuration_id: data.configuration_id,
        credential_id:    data.credential_id
      });
      if (onRefreshed) onRefreshed(res.data);
      clearSelection();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setRefreshing(false);
    }
  }

  async function handleConfirmDelete() {
    if (selected.size === 0) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const paths = [...selected];
      const res = await axios.post('/api/bulloch/sftp-delete', {
        credential_id: data.credential_id,
        paths
      });
      // Even if some files failed, refresh so the UI catches up.
      if (res.data.failed_count > 0) {
        setDeleteError(
          `${res.data.deleted_count} deleted, ${res.data.failed_count} failed. ` +
          res.data.results.filter((r) => !r.ok).map((r) => `${r.path}: ${r.error}`).join('; ')
        );
      } else {
        setDeleteOpen(false);
      }
      clearSelection();
      await handleRefresh();
    } catch (err) {
      setDeleteError(err.response?.data?.error || err.message);
    } finally {
      setDeleting(false);
    }
  }

  function handleDownloadCsv() {
    const files = collectFiles(folders, extFilter);
    const header = ['Folder', 'Path', 'Name', 'Extension', 'Size (bytes)', 'Last Modified'];
    const rows = [header, ...files.map((f) => [
      f.folder, f.path, f.name, f.ext, f.size ?? '', formatModifyTime(f.modify_time)
    ])];
    const csv = toCsv(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const today = new Date().toISOString().slice(0, 10);
    const labelPart = extFilter === 'ALL' ? 'all' : extFilter.toLowerCase();
    const filename = `sftp-${data.username || 'user'}-${labelPart}-${today}.csv`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  if (!data || !data.ok) return null;

  const filteredFileCount = collectFiles(folders, extFilter).length;
  const sortLabel = `${sortBy === 'name' ? 'Name' : sortBy === 'size' ? 'Size' : 'Modified'} ${sortDir === 'asc' ? '↑' : '↓'}`;

  return (
    <section className="sftp-browser">
      <header className="sftp-browser-head">
        <div>
          <h3>SFTP Directory</h3>
          <p className="sftp-browser-sub">
            Connected to <code>{data.username}@{data.host}</code>
            {data.port && data.port !== 22 && <> on port <code>{data.port}</code></>}
            {' · '}response {data.response_time_ms} ms
            {extFilter !== 'ALL' && <> · filter: <strong>{extFilter}</strong> ({filteredFileCount} files)</>}
          </p>
        </div>
        <button type="button" className="btn-secondary btn-sm" onClick={handleRefresh} disabled={refreshing}>
          <RefreshCw size={14} strokeWidth={1.75} />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </header>

      {/* Toolbar — filter, sort, selection actions */}
      <div className="sftp-toolbar">
        <label className="sftp-toolbar-field">
          <span>File type</span>
          <select value={extFilter} onChange={(e) => setExtFilter(e.target.value)}>
            {filterOptions.map((opt) => (
              <option key={opt} value={opt}>{opt === 'ALL' ? 'All files' : opt}</option>
            ))}
          </select>
        </label>
        <label className="sftp-toolbar-field">
          <span>Sort by</span>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="name">Name</option>
            <option value="size">Size</option>
            <option value="date">Last modified</option>
          </select>
        </label>
        <label className="sftp-toolbar-field">
          <span>Direction</span>
          <select value={sortDir} onChange={(e) => setSortDir(e.target.value)}>
            <option value="asc">Asc</option>
            <option value="desc">Desc</option>
          </select>
        </label>
        <span className="sftp-toolbar-sortlabel cell-mono">{sortLabel}</span>
        <span className="sftp-toolbar-spacer" />
        <button
          type="button"
          className="btn-secondary btn-sm"
          onClick={handleDownloadCsv}
          disabled={filteredFileCount === 0}
          title="Download the filtered file list as CSV"
        >
          <Download size={14} strokeWidth={1.75} />
          Download CSV
        </button>
        <button
          type="button"
          className="btn-secondary btn-sm sftp-delete-button"
          onClick={() => { setDeleteError(null); setDeleteOpen(true); }}
          disabled={selected.size === 0}
          title={selected.size === 0 ? 'Select one or more files first' : 'Delete selected files from SFTP'}
        >
          <Trash2 size={14} strokeWidth={1.75} />
          Delete{selected.size > 0 ? ` (${selected.size})` : ''}
        </button>
      </div>

      {error && <p className="error" style={{ marginTop: 0 }}>{error}</p>}

      <FolderSection
        title="Send"    folder={folders.Send}
        isOpen={!collapsedTop.has('Send')}    onToggle={() => toggleTop('Send')}
        sortBy={sortBy} sortDir={sortDir} extFilter={extFilter}
        selected={selected} onToggleSelect={toggleSelect}
        collapsedSubs={collapsedSubs} toggleSub={toggleSub}
      />
      <FolderSection
        title="Receive" folder={folders.Receive}
        isOpen={!collapsedTop.has('Receive')} onToggle={() => toggleTop('Receive')}
        sortBy={sortBy} sortDir={sortDir} extFilter={extFilter}
        selected={selected} onToggleSelect={toggleSelect}
        collapsedSubs={collapsedSubs} toggleSub={toggleSub}
      />

      <details className="sftp-root-details">
        <summary>Root directory ({data.root.length} {data.root.length === 1 ? 'item' : 'items'})</summary>
        <EntryTable
          entries={data.root}
          parentPath=""
          sortBy={sortBy} sortDir={sortDir}
          extFilter={extFilter}
          selected={selected} onToggleSelect={toggleSelect}
        />
      </details>

      <DeleteConfirmDialog
        open={deleteOpen}
        paths={[...selected]}
        busy={deleting}
        error={deleteError}
        onCancel={() => { if (!deleting) { setDeleteOpen(false); setDeleteError(null); } }}
        onConfirm={handleConfirmDelete}
      />
    </section>
  );
}
